import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Target, ChevronRight, RotateCcw } from 'lucide-react'
import { SectionGrid } from '../SectionGrid'
import { PrerequisiteWarning } from '../PrerequisiteWarning'
import { exercisesAPI } from '../../../api/exercises'
import { useCoursePrerequisites } from '../../../hooks/useCoursePrerequisites'
import { isExerciseComplete, type PendingPrerequisite } from '../../../lib/prerequisites'
import type { ExerciseDetail } from '../../../types/api'

const GOLD = '#7a6020'
const GOLD_LIGHT = '#D4AF37'
const GOLD_TINT = '#f5ede0'
const GOLD_BORDER = '#e0d4be'

interface ExerciseWithProgress extends ExerciseDetail {
  progress: number
  status: string
  tasks_completed: number
  total_tasks: number
}

interface ExerciseCardProps {
  exercise: ExerciseWithProgress
  index: number
  isResetting: boolean
  onClick: () => void
  onReset: (event: React.MouseEvent) => void
}

const ExerciseCard = ({
  exercise,
  index,
  isResetting,
  onClick,
  onReset
}: ExerciseCardProps): React.JSX.Element => {
  const progress = exercise.progress ?? 0
  const isStarted = exercise.status !== 'not_started'
  const isCompleted = exercise.status === 'completed' || progress >= 100
  const taskCount = exercise.total_tasks || exercise.tasks?.length || 0
  const exerciseLabel = `E${exercise.order_index ?? index + 1}`

  const blurb =
    exercise.objectives?.[0]?.text ||
    'Work through this hands-on lab exercise with guidance from your instructor.'

  const badge = isCompleted
    ? { text: 'Completed', bg: GOLD_TINT, color: GOLD }
    : isStarted
      ? { text: 'In progress', bg: GOLD_TINT, color: GOLD }
      : null

  return (
    <div
      className="flex-1 min-w-[240px] max-w-[280px] rounded-xl p-3.5 flex flex-col gap-2 cursor-pointer transition-all"
      style={{
        background: '#ffffff',
        border: isStarted && !isCompleted ? `1.5px solid ${GOLD_LIGHT}` : `1px solid ${GOLD_BORDER}`
      }}
      onClick={onClick}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold" style={{ color: GOLD }}>{exerciseLabel}</span>
        <div className="flex items-center gap-1.5">
          {badge && (
            <span
              className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
              style={{ background: badge.bg, color: badge.color }}
            >
              {badge.text}
            </span>
          )}
          {isStarted && (
            <button
              onClick={onReset}
              disabled={isResetting}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
              title="Reset exercise progress"
            >
              <RotateCcw
                className={`w-3.5 h-3.5 text-gray-500 group-hover:text-gray-800 transition-colors ${
                  isResetting ? 'animate-spin' : ''
                }`}
              />
            </button>
          )}
        </div>
      </div>

      <h3 className="text-sm font-bold text-gray-900 leading-snug">{exercise.title}</h3>

      <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{blurb}</p>

      {taskCount > 0 && (
        <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
          <Target className="w-3 h-3" />
          <span>{exercise.tasks_completed ?? 0}/{taskCount} tasks</span>
        </div>
      )}

      {isStarted && !isCompleted && (
        <div className="h-1 bg-gray-200 rounded-full overflow-hidden mt-1">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${progress}%`, background: GOLD }}
          />
        </div>
      )}

      {isCompleted ? (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onClick()
          }}
          className="mt-1 w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border text-xs font-medium transition-colors hover:bg-gray-50"
          style={{ borderColor: GOLD_BORDER, color: '#374151' }}
        >
          Review Exercise <ChevronRight className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onClick()
          }}
          className="mt-1 w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold text-white transition-colors hover:opacity-90"
          style={{ background: GOLD }}
        >
          {isStarted ? 'Resume Exercise' : 'Start Exercise'}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}

interface AvailableExercisesProps {
  moduleId: string | undefined
  courseId?: string
  onExercisesLoaded?: (exercises: ExerciseWithProgress[]) => void
}

interface PendingWarning {
  exercise: ExerciseWithProgress
  prerequisites: PendingPrerequisite[]
}

export const AvailableExercises: React.FC<AvailableExercisesProps> = ({
  moduleId,
  courseId,
  onExercisesLoaded
}) => {
  const navigate = useNavigate()
  const [exercises, setExercises] = useState<ExerciseWithProgress[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resetting, setResetting] = useState<Set<number>>(new Set())
  const [warning, setWarning] = useState<PendingWarning | null>(null)

  const { getPendingPrerequisites } = useCoursePrerequisites({
    courseId,
    moduleId,
    moduleExercises: exercises
  })

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!moduleId) {
        setLoading(false)
        return
      }
      try {
        const data = await exercisesAPI.getModuleExercises(moduleId)
        setExercises(data)
        onExercisesLoaded?.(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [moduleId])

  const openExercise = (exerciseId: number): void => {
    setWarning(null)
    navigate(`/exercise/${exerciseId}/chat`)
  }

  /**
   * Warn before opening an exercise that has unfinished exercises ahead of it
   * in the course — the learner can still continue if they want to.
   */
  const handleExerciseClick = (exercise: ExerciseWithProgress): void => {
    if (isExerciseComplete(exercise)) {
      openExercise(exercise.id)
      return
    }
    const prerequisites = getPendingPrerequisites(exercise.id)
    if (prerequisites.length === 0) {
      openExercise(exercise.id)
      return
    }
    setWarning({ exercise, prerequisites })
  }

  const handleReset = async (
    exerciseId: number,
    event: React.MouseEvent
  ): Promise<void> => {
    event.stopPropagation()

    setResetting((prev) => new Set(prev).add(exerciseId))
    try {
      await exercisesAPI.resetExercise(exerciseId)
      setExercises((prev) => {
        const next = prev.map((ex) =>
          ex.id === exerciseId
            ? {
                ...ex,
                status: 'not_started',
                progress: 0,
                tasks_completed: 0
              }
            : ex
        )
        onExercisesLoaded?.(next)
        return next
      })
    } catch (err) {
      console.error('Error resetting exercise:', err)
      alert('Failed to reset exercise progress. Please try again.')
    } finally {
      setResetting((prev) => {
        const next = new Set(prev)
        next.delete(exerciseId)
        return next
      })
    }
  }

  return (
    <>
      <SectionGrid
        title="Available Exercises"
        subtitle="Put your knowledge to the test with hands-on lab exercises."
        loading={loading}
        error={error}
        emptyText="No exercises found for this module."
        loadingText="Loading exercises..."
        gridClassName="flex w-full gap-3 items-stretch flex-wrap"
      >
        {exercises.map((exercise, idx) => (
          <ExerciseCard
            key={exercise.id}
            exercise={exercise}
            index={idx}
            isResetting={resetting.has(exercise.id)}
            onClick={() => handleExerciseClick(exercise)}
            onReset={(e) => handleReset(exercise.id, e)}
          />
        ))}
      </SectionGrid>

      {warning && (
        <PrerequisiteWarning
          exerciseTitle={warning.exercise.title}
          prerequisites={warning.prerequisites}
          onGoToPrerequisite={openExercise}
          onContinueAnyway={() => openExercise(warning.exercise.id)}
          onClose={() => setWarning(null)}
        />
      )}
    </>
  )
}
